import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TestimonialSubmission.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost/ehaazri/api';

const TestimonialSubmission = () => {
    const MAX_TESTIMONIAL_LENGTH = 300;
    const [formData, setFormData] = useState({
        client_position: '',
        testimonial_text: '',
        rating: 5
    });
    const [myTestimonials, setMyTestimonials] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [showForm, setShowForm] = useState(true);

    useEffect(() => {
        loadMyTestimonials();
    }, []);

    const loadMyTestimonials = async () => {
        try {
            const token = localStorage.getItem('haazri_token');
            const response = await axios.get(`${API_URL}/landing_page_api.php/testimonials/my-org`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.data.status === 'success') {
                setMyTestimonials(response.data.data || []);
            }
        } catch (error) {
            console.error('Failed to load testimonials:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'testimonial_text') {
            setFormData(prev => ({
                ...prev,
                [name]: value.slice(0, MAX_TESTIMONIAL_LENGTH)
            }));
            return;
        }
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        console.log('=== TESTIMONIAL SUBMIT DEBUG ===');
        console.log('Form Data:', formData);
        console.log('API URL:', `${API_URL}/landing_page_api.php/testimonials/submit`);

        try {
            const token = localStorage.getItem('haazri_token');
            console.log('Token exists:', !!token);

            const response = await axios.post(
                `${API_URL}/landing_page_api.php/testimonials/submit`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            console.log('Response:', response.data);

            if (response.data.status === 'success') {
                setMessage({ type: 'success', text: response.data.message });
                setFormData({
                    client_position: '',
                    testimonial_text: '',
                    rating: 5
                });
                setShowForm(false);
                loadMyTestimonials();
            } else {
                console.error('Submit failed with message:', response.data.message);
                setMessage({ type: 'error', text: response.data.message });
            }
        } catch (error) {
            console.error('=== TESTIMONIAL SUBMIT ERROR ===');
            console.error('Error:', error);
            console.error('Error Response:', error.response);
            console.error('Error Data:', error.response?.data);
            console.error('Error Status:', error.response?.status);

            const errorMessage = error.response?.data?.message || error.message || 'Failed to submit testimonial';
            setMessage({ type: 'error', text: errorMessage });
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            pending: { class: 'badge-warning', text: 'Pending Review' },
            approved: { class: 'badge-success', text: 'Approved' },
            rejected: { class: 'badge-danger', text: 'Rejected' }
        };
        const badge = badges[status] || badges.pending;
        return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
    };

    const renderStars = (rating) => {
        return [...Array(5)].map((_, index) => (
            <i
                key={index}
                className={`fas fa-star ${index < rating ? 'star-filled' : 'star-empty'}`}
            ></i>
        ));
    };

    return (
        <div className="testimonial-submission">
            <div className="page-header">
                <h2><i className="fas fa-star"></i> Submit Testimonial</h2>
                <p>Share your experience with Haazri to help others learn about our service</p>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
                    {message.text}
                </div>
            )}

            {showForm ? (
                <div className="testimonial-form-card">
                    <h3>New Testimonial</h3>
                    <form onSubmit={handleSubmit}>
                        <div className="form-grid">
                            <div className="form-group">
                                <label>Your Position <span className="required">*</span></label>
                                <input
                                    type="text"
                                    name="client_position"
                                    value={formData.client_position}
                                    onChange={handleChange}
                                    required
                                    placeholder="e.g., HR Manager"
                                />
                            </div>

                            <div className="form-group">
                                <label>Rating <span className="required">*</span></label>
                                <div className="rating-selector">
                                    {[1, 2, 3, 4, 5].map(star => (
                                        <button
                                            key={star}
                                            type="button"
                                            className={`star-btn ${formData.rating >= star ? 'active' : ''}`}
                                            onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                                        >
                                            <i className="fas fa-star"></i>
                                        </button>
                                    ))}
                                    <span className="rating-text">{formData.rating} out of 5</span>
                                </div>
                            </div>
                        </div>

                        <div className="form-group full-width">
                            <label>Your Testimonial <span className="required">*</span></label>
                            <textarea
                                name="testimonial_text"
                                value={formData.testimonial_text}
                                onChange={handleChange}
                                required
                                rows="5"
                                maxLength={MAX_TESTIMONIAL_LENGTH}
                                placeholder="Share your experience with Haazri..."
                            ></textarea>
                            <small>
                                Maximum {MAX_TESTIMONIAL_LENGTH} characters ({formData.testimonial_text.length}/{MAX_TESTIMONIAL_LENGTH})
                            </small>
                        </div>

                        <div className="form-actions">
                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                {loading ? (
                                    <><i className="fas fa-spinner fa-spin"></i> Submitting...</>
                                ) : (
                                    <><i className="fas fa-paper-plane"></i> Submit Testimonial</>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            ) : (
                <div className="success-message">
                    <i className="fas fa-check-circle"></i>
                    <h3>Testimonial Submitted Successfully!</h3>
                    <p>Your testimonial has been submitted and is pending review by our team.</p>
                    <button className="btn btn-primary" onClick={() => setShowForm(true)}>
                        <i className="fas fa-plus"></i> Submit Another Testimonial
                    </button>
                </div>
            )}

            <div className="my-testimonials-section">
                <h3><i className="fas fa-list"></i> My Testimonials</h3>
                {myTestimonials.length === 0 ? (
                    <div className="empty-state">
                        <i className="fas fa-inbox"></i>
                        <p>You haven't submitted any testimonials yet</p>
                    </div>
                ) : (
                    <div className="testimonials-list">
                        {myTestimonials.map(testimonial => (
                            <div key={testimonial.id} className="testimonial-card">
                                <div className="testimonial-header">
                                    <div className="testimonial-info">
                                        <h4>{testimonial.client_name}</h4>
                                        <p>{testimonial.client_position} at {testimonial.client_company}</p>
                                    </div>
                                    <div className="testimonial-meta">
                                        {getStatusBadge(testimonial.submission_status)}
                                        <div className="rating-display">
                                            {renderStars(testimonial.rating)}
                                        </div>
                                    </div>
                                </div>
                                <div className="testimonial-body">
                                    <p>{testimonial.testimonial_text}</p>
                                </div>
                                <div className="testimonial-footer">
                                    <small>
                                        <i className="fas fa-calendar"></i> Submitted: {new Date(testimonial.submitted_at || testimonial.created_at).toLocaleDateString()}
                                    </small>
                                    {testimonial.submission_status === 'rejected' && testimonial.rejection_reason && (
                                        <div className="rejection-reason">
                                            <i className="fas fa-info-circle"></i>
                                            <strong>Rejection Reason:</strong> {testimonial.rejection_reason}
                                        </div>
                                    )}
                                    {testimonial.submission_status === 'approved' && testimonial.is_active && (
                                        <small className="published-badge">
                                            <i className="fas fa-globe"></i> Published on landing page
                                        </small>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TestimonialSubmission;
